/**
 * Card play queue — buffers hand-card clicks during trick play and fires them
 * as ordinary play-card submits when the player's turn arrives. Client-side UI
 * intent only, never a game fact: every fire is a normal submit, validated
 * server-side like any manual play. The queue dies entirely (never partial
 * removal) when any reset rule fires; a reset is silent and cheap — the player
 * re-clicks. Spec: specs/2026-06/2026-06-04-card-play-queue-design.md.
 */
import { api } from "./api.js";
import {
  getApp,
  getCurrentState,
  isAnimating,
  isChainHalted,
} from "./state.js";
import { screenPositionFor, spectatedPlayer, isViewerTurn } from "./utils.js";

const QUEUE_FIRE_DELAY_MS = 200; // play-test: beat between turn arrival and the queued head firing — gives the eye a moment to register the turn before the card moves

let queue = []; // [{ code, num }] — card codes, never DOM nodes (badges project by data-code)
let nextNumber = 1; // monotonic per queue lifetime, never reshuffled
let fireTimer = null; // scheduled head fire; non-null = drain armed (the once-per-turn guard)
let inFlightSubmit = false; // a play-card submit awaiting its response — clicks during it enqueue instead of double-submitting

// --- click entry (wireHandClicks routes every trick-play hand-card click here) ---

// Returns true iff an immediate play-card submit fired — i.e. the card is on its
// way to the trick now. Callers that lifted the card out of the fan (the drag
// affordance) read this to decide whether to hand off to the play-flight (true)
// or settle the card back home with its queue badge (false: enqueued, inert, or
// toggled off). The click path ignores it (the card never left the fan).
export function onHandCardClick(code) {
  const state = getCurrentState();
  if (state?.phase !== "PLAYING") return false;
  if (queue.some((e) => e.code === code)) {
    resetCardQueue(); // reset rule 2 — clicking a queued card doubles as cancel
    return false;
  }
  if (canFireImmediately(state)) {
    const card = spectatedPlayer(state)?.cards?.find((c) => c.code === code);
    // An illegal card in the immediate context stays inert (today's behavior):
    // "play as soon as it's my turn" is unsatisfiable for a card illegal now.
    if (card?.isLegal) {
      submitPlay(code);
      return true;
    }
    return false;
  }
  queue.push({ code, num: nextNumber++ });
  projectQueueBadges();
  return false;
}

// The degenerate case: empty queue + settled player's turn → the click fires
// immediately, exactly as before the queue existed. Mid-burst clicks (state
// advanced early, board still animating — api() would drop them) and clicks
// during an in-flight submit are NOT immediate: they enqueue and drain at the
// next settled turn arrival. isViewerTurn is the multiplayer defense-in-depth
// (debug-first-multiplayer-issues): AWAIT_CARD_PLAY reaches every subscriber, so
// without it an off-turn seat's click on their own determined-legal card would
// submit immediately and 400 at RoundDriver.validateActor — off-turn it enqueues
// instead, same as a mid-burst click, and drains once the viewer's own turn arrives.
function canFireImmediately(state) {
  return (
    queue.length === 0 &&
    !inFlightSubmit &&
    state.pendingAction === "AWAIT_CARD_PLAY" &&
    isViewerTurn(state) &&
    !isAnimating()
  );
}

function submitPlay(code) {
  inFlightSubmit = true;
  return api("submit", { type: "play-card", card: code }).finally(() => {
    inFlightSubmit = false;
  });
}

// --- drain entry (dispatch handoff calls this at settled turn arrival) ---

// Arm the head fire once per turn arrival. The fireTimer guard ensures the
// head fires exactly once even when clicks and dispatch events race.
export function maybeDrainCardQueue() {
  if (queue.length === 0 || fireTimer != null) return;
  fireTimer = setTimeout(fireHead, QUEUE_FIRE_DELAY_MS);
}

async function fireHead() {
  fireTimer = null;
  // why: a halted chain (recoverFromFatalError froze the board for the reload it triggers) must
  // suppress an already-armed fire too — the modal shield it mounts makes the board inert to
  // pointer and keyboard, but a timer armed by the settle just before the crash is deaf to a
  // dialog and would POST a play off a board the renderer declared inconsistent. Bare return, no
  // reset: the page is leaving, so emptying a queue that is about to vanish buys nothing. Same
  // signal bid-pipeline.js#drain and api.js#applyServerState honor.
  if (isChainHalted()) return;
  const state = getCurrentState();
  // Multiplayer: AWAIT_CARD_PLAY rests at every OTHER human seat's turn too (still the
  // same pendingAction, debug-first-multiplayer-issues) — that's not a reset-worthy
  // desync, just not this seat's turn YET. Wait rather than drop: leave the queue
  // intact and re-arm at the next settle (maybeDrainCardQueue's fireTimer guard is
  // cleared above), until a settle actually names this seat as awaited.
  if (state?.pendingAction === "AWAIT_CARD_PLAY" && !isViewerTurn(state))
    return;
  const head = queue[0];
  const card = spectatedPlayer(state)?.cards?.find((c) => c.code === head.code);
  // Reset rule 4: an illegal (or vanished) head at turn arrival resets instead
  // of submitting an illegal play. Reads the live post-projection state.
  if (
    state?.pendingAction !== "AWAIT_CARD_PLAY" ||
    isAnimating() ||
    card?.isLegal !== true
  ) {
    resetCardQueue();
    return;
  }
  // Dequeue BEFORE the submit: the response's burst — and the NEXT turn's
  // drain — runs nested inside this await and must see the queue without the
  // fired head. Numbering is untouched (monotonic per queue lifetime).
  queue.shift();
  projectQueueBadges();
  let ok = false;
  try {
    ok = await submitPlay(head.code);
  } catch (e) {
    // Recovery, not suppression: a network-failed fire is reset rule 6.
    console.warn("[card-queue] queue-fired submit failed:", e?.message ?? e);
  }
  if (!ok) resetCardQueue(); // reset rule 6 — failed/raced submit, back to manual
}

// --- reset rules ---

export function resetCardQueue() {
  if (fireTimer != null) clearTimeout(fireTimer);
  fireTimer = null;
  queue = [];
  nextNumber = 1;
  projectQueueBadges();
}

// Reset rule 1: a completed trick the player didn't win breaks the "I'm in
// control" assumption. Called by the TrickResolved mutation (trick-resolve.js)
// — mutations legitimately invalidate stale interaction state (DOM
// § Three client-state layers).
export function onTrickResolved(next) {
  const winner = next.lastCompletedTrick?.winnerName;
  if (winner != null && screenPositionFor(winner, next) !== "human") {
    resetCardQueue();
  }
}

const isHandCard = (target) => target.closest(".hand .card[data-code]") != null;

// Did the primary pointer's press behind the next click start on a hand card.
// One bit, not a per-pointerId map: `click` is a PointerEvent carrying pointerId
// only in Chromium — in Safari and Firefox it is still a plain MouseEvent where
// pointerId is undefined, and this bug is phone-shaped, so that signal is
// unreliable exactly where it has to work.
let pressedHandCard = false;

// Reset rule 3: clicking anything that isn't a hand card resets — one
// delegated document-level predicate. Exceptions get carved out when a
// concrete passive control earns one (spec § Out of scope).
//
// "Isn't a hand card" is judged by where the press STARTED, not only by the
// click's own hit-test target. why: a tap lifts the card into the drag grip, and
// on a cramped fan (phone portrait) the card can leave the tap point — the
// browser then synthesizes the click at that point, where a plain <div> now sits,
// and the tap that just queued a card read as "clicked elsewhere" and discarded
// the whole queue. Touch-only: with a mouse the click follows pointer capture to
// the card.
//
// Three guards keep that one bit correlated with the click that reads it — a
// stale-true flag would make a genuine click-elsewhere skip its reset:
//  1. only the primary pointer's primary button arms it. A middle/right press
//     produces auxclick/contextmenu and never a click, so nothing would consume
//     the flag it set; and a second simultaneous finger (isPrimary false) can no
//     longer clobber the first finger's memory.
//  2. pointercancel clears it, under the same primary gate — the browser taking
//     the gesture over for a scroll ends the sequence with no click coming.
//  3. a keyboard-activated click never reads it: detail === 0 is the
//     cross-browser mark of an activation with no pointer behind it
//     (UIEvent.detail is the click count for pointer-driven clicks). The flag is
//     cleared on every click either way.
// After those, every pointer-driven click is preceded by its own primary
// pointerdown, which overwrites the flag, and no keyboard click can read one.
// The accepted residue is the multi-touch case guard 1 shifts rather than
// removes: a second finger pressing outside while the first holds a hand card
// has its pointerdown ignored, so its own click reads the armed flag and skips a
// reset it should have done. That direction is deliberate — losing a queue the
// player built is the user-visible harm, while a missed reset is silent and
// cheap (the five other reset rules still fire, and rule 4 rejects an illegal
// head at turn arrival).
export function initCardQueue() {
  document.addEventListener("pointerdown", (e) => {
    if (!e.isPrimary || e.button !== 0) return;
    pressedHandCard = isHandCard(e.target);
  });
  document.addEventListener("pointercancel", (e) => {
    if (!e.isPrimary) return;
    pressedHandCard = false;
  });
  document.addEventListener("click", (e) => {
    const fromPress = pressedHandCard && e.detail > 0;
    pressedHandCard = false;
    if (queue.length === 0) return;
    if (fromPress || isHandCard(e.target)) return;
    resetCardQueue();
  });
}

// --- badge projection (by data-code onto the persistent hand, like legality) ---

function projectQueueBadges() {
  const hand = getApp()?.querySelector(":scope > .hand");
  if (hand == null) return;
  const byCode = new Map(queue.map((e) => [e.code, e.num]));
  for (const cardEl of hand.querySelectorAll(":scope > .card[data-code]")) {
    const num = byCode.get(cardEl.dataset.code);
    if (num == null) {
      cardEl.classList.remove("queued");
      delete cardEl.dataset.queuePos;
    } else {
      cardEl.classList.add("queued");
      cardEl.dataset.queuePos = String(num);
    }
  }
}
